import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { AuthService, User } from '@auth0/auth0-angular';
import { NgxPermissionsService, NgxRolesService } from 'ngx-permissions';
import { firstValueFrom } from 'rxjs';
import { AppContextService } from './app-context.service';
import { Menu, MenuService } from './menu.service';
import {
  GymExistenceGQL,
  GymType,
  CommunityClubExistenceGQL,
} from '@graphql/generated/graphql';

export const BUSINESS_OWNER_ROLE = 'company-business-owner-role';

export const MENU: Menu[] = [
  {
    route: 'dashboard',
    name: 'dashboard',
    type: 'link',
    icon: 'dashboard',
    permissions: {
      only: ['company-business-owner-role'],
    },
  },
  {
    route: 'gym-management',
    name: 'gymManagement',
    type: 'sub',
    icon: 'store',
    permissions: {
      only: ['company-business-owner-role'],
    },
    children: [
      {
        route: 'gyms',
        name: 'gyms',
        type: 'link',
        icon: 'store',
        permissions: {
          only: ['company-business-owner-role'],
        },
      },
      {
        route: 'ratings',
        name: 'ratings',
        type: 'link',
        icon: 'star',
        permissions: {
          only: ['company-business-owner-role'],
        },
      },
    ],
  },
  {
    route: 'customers',
    name: 'customers',
    type: 'link',
    icon: 'person',
    permissions: {
      only: ['company-business-owner-role'],
    },
  },
  {
    route: 'messages',
    name: 'messages',
    type: 'link',
    icon: 'chat',
  },
  {
    route: 'training-sessions',
    name: 'trainingSessions',
    type: 'link',
    icon: 'fitness_center',
  },
  {
    route: 'marketing',
    name: 'marketing',
    type: 'sub',
    icon: 'auto_graph',
    permissions: {
      only: ['company-business-owner-role'],
    },
    children: [
      {
        route: 'push-notification',
        name: 'pushNotification',
        icon: '',
        type: 'link',
        permissions: {
          only: ['company-business-owner-role'],
        },
      },
      {
        route: 'promotion',
        name: 'promotion',
        icon: '',
        type: 'link',
        permissions: {
          only: ['company-business-owner-role'],
        },
      },
      {
        route: 'promocodes',
        name: 'promocodes',
        icon: '',
        type: 'link',
        permissions: {
          only: ['company-business-owner-role'],
        },
      },
      {
        route: 'studio-highlight',
        name: 'studioHighlight',
        icon: '',
        type: 'link',
        permissions: {
          only: ['company-business-owner-role'],
        },
      },
    ],
  },
  {
    name: 'helpCenter',
    route: 'help-center',
    type: 'sub',
    icon: 'help_outline',
    children: [
      {
        name: 'guides',
        route: 'guides',
        icon: '',
        type: 'link',
      },
      {
        name: 'support',
        route: 'support',
        icon: '',
        type: 'link',
      },
    ],
  },
  {
    route: 'settings',
    name: 'settings',
    type: 'sidenavSub',
    icon: 'settings',
    children: [
      {
        name: 'account',
        type: 'link',
        icon: 'person',
        route: 'account',
        description: 'menu.settings.account.description',
      },
      {
        name: 'company',
        type: 'link',
        icon: 'business',
        route: 'company',
        description: 'menu.settings.company.description',
        permissions: {
          only: ['company-business-owner-role'],
        },
      },

      {
        name: 'team',
        type: 'link',
        icon: 'groups',
        route: 'team',
        description: 'menu.settings.team.description',
        permissions: {
          only: ['company-business-owner-role'],
        },
      },
    ],
  },
];

@Injectable({
  providedIn: 'root',
})
export class StartupService {
  private readonly _authService = inject(AuthService);
  private readonly _menuService = inject(MenuService);
  private readonly _rolesService = inject(NgxRolesService);
  private readonly _permissionsService = inject(NgxPermissionsService);
  private readonly _title = inject(Title);
  private readonly _gymExistenceGQL = inject(GymExistenceGQL);
  private readonly _communityClubExistenceGQL = inject(
    CommunityClubExistenceGQL
  );
  private readonly _appContext = inject(AppContextService);

  // private readonly _rolesKey = 'userRoles';
  private readonly _permissionKey = 'userRoles';

  async load(): Promise<void> {
    try {
      const user = await firstValueFrom(this._authService.user$);
      this._setPermissions(user);

      let menu = structuredClone(MENU);

      if (user) {
        const isBusinessOwner =
          BUSINESS_OWNER_ROLE in this._permissionsService.getPermissions();

        const allGymsExistence = await firstValueFrom(
          this._gymExistenceGQL.fetch()
        );
        const hasGyms = (allGymsExistence.data.gyms?.length ?? 0) > 0;

        const specialInterestExistence = await firstValueFrom(
          this._gymExistenceGQL.fetch({
            where: { type: { equals: GymType.SpecialInterestStudio } },
          })
        );
        const hasSpecialInterest =
          (specialInterestExistence.data.gyms?.length ?? 0) > 0;

        const communityClubExistence = await firstValueFrom(
          this._communityClubExistenceGQL.fetch()
        );
        const hasCommunityClubs =
          (communityClubExistence.data.communityClubs?.length ?? 0) > 0;

        const showGymMenu = hasGyms || (!hasGyms && !hasCommunityClubs);

        this._appContext.hasGyms = hasGyms;
        this._appContext.setCommunityClubs(
          communityClubExistence.data.communityClubs ?? []
        );

        if (!showGymMenu) {
          menu = menu.filter(
            m =>
              !['dashboard', 'gymManagement', 'trainingSessions'].includes(
                m.name
              )
          );

          const marketing = menu.find(m => m.name === 'marketing');
          if (marketing && marketing.type === 'sub') {
            marketing.children = marketing.children.filter(
              child => child.name === 'pushNotification'
            );
          }
        }

        /* messages are only relevant for companies with community clubs */
        if (!hasCommunityClubs) {
          menu = menu.filter(m => m.name !== 'messages');
        }

        /* employees only get access to push notifications, and only if the company has
         * community clubs (they can only send notifications restricted to one) */
        if (!isBusinessOwner) {
          const marketingIndex = menu.findIndex(m => m.name === 'marketing');
          const marketing = menu[marketingIndex];
          if (marketing && marketing.type === 'sub') {
            if (hasCommunityClubs) {
              marketing.permissions = undefined;
              marketing.children = marketing.children
                .filter(child => child.name === 'pushNotification')
                .map(child => ({ ...child, permissions: undefined }));
            } else {
              menu.splice(marketingIndex, 1);
            }
          }
        }

        if (hasSpecialInterest) {
          const customersIndex = menu.findIndex(m => m.name === 'customers');
          const insertIndex =
            customersIndex > -1 ? customersIndex : menu.length;

          menu.splice(insertIndex, 0, {
            route: 'event-management',
            name: 'eventManagement',
            type: 'sub',
            icon: 'calendar_month',
            permissions: {
              only: ['company-business-owner-role'],
            },
            children: [
              {
                route: 'events',
                name: 'events',
                type: 'link',
                icon: 'event',
                permissions: {
                  only: ['company-business-owner-role'],
                },
              },
              {
                route: 'event-bookings',
                name: 'eventBookings',
                type: 'link',
                icon: 'calendar_month',
                permissions: {
                  only: ['company-business-owner-role'],
                },
              },
            ],
          });
        }

        if (hasCommunityClubs || (!hasGyms && !hasCommunityClubs)) {
          const customersIndex = menu.findIndex(m => m.name === 'customers');
          const insertIndex =
            customersIndex > -1 ? customersIndex : menu.length;

          menu.splice(insertIndex, 0, {
            route: 'community-management',
            name: 'community',
            type: 'sub',
            icon: 'groups',
            children: [
              {
                route: 'community-clubs',
                name: 'clubs',
                type: 'link',
                icon: 'holiday_village',
                permissions: {
                  only: ['company-business-owner-role'],
                },
              },
              {
                route: 'community-events',
                name: 'events',
                type: 'link',
                icon: 'event',
              },
              {
                route: 'community-event-partner',
                name: 'partner',
                type: 'link',
                icon: 'handshake',
                permissions: {
                  only: ['company-business-owner-role'],
                },
              },
              {
                route: 'community-event-location',
                name: 'location',
                type: 'link',
                icon: 'location_on',
              },
              {
                route: 'community-posts',
                name: 'posts',
                type: 'link',
                icon: 'post_add',
              },
            ],
          });
        }

        /* companies with only community clubs (no gyms) call their customers "members" */
        if (!showGymMenu) {
          const customers = menu.find(m => m.name === 'customers');
          if (customers) {
            customers.name = 'members';
          }
        }
      }

      this._setMenu(menu);
      this._setTitle(user);
    } catch (error) {
      throw new Error('Error during startup');
    }
  }

  private _setMenu(menu: Menu[]): void {
    this._menuService.addNamespace(menu, 'menu');
    this._menuService.set(menu);
  }

  private _setPermissions(user: User | null | undefined): void {
    if (user) {
      const permissions = user[this._permissionKey] as {
        [name: string]: string[];
      };
      if (permissions) {
        this._rolesService.addRolesWithPermissions(permissions);
      }
    }
  }

  private _setTitle(user: User | null | undefined): void {
    if (user?.['org_name']) {
      this._title.setTitle(`${user['org_name']} - TrainToGo`);
    }
  }
}
